// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  I18nContext,
  type Locale,
  type TranslateFn,
  type TranslationParams,
} from '@/contexts/i18n';
import { locales } from '@/locales';

function detectLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  const saved = window.localStorage.getItem('locale');
  if (saved === 'zh' || saved === 'en') return saved;
  const nav = window.navigator.language?.toLowerCase() || '';
  return nav.startsWith('zh') ? 'zh' : 'en';
}

function format(template: string, params?: TranslationParams) {
  if (!params) return template;
  let out = template;
  for (const [k, v] of Object.entries(params)) {
    out = out.split(`{${k}}`).join(String(v));
  }
  return out;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale());

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem('locale', next);
    } catch {
      void 0;
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback<TranslateFn>(
    (
      key: string,
      fallbackOrParams?: string | TranslationParams,
      maybeParams?: TranslationParams
    ) => {
      const fallback = typeof fallbackOrParams === 'string' ? fallbackOrParams : undefined;
      const params = typeof fallbackOrParams === 'object' ? fallbackOrParams : maybeParams;
      const dict = locales[locale];
      const raw = dict[key] ?? locales.en[key] ?? fallback ?? key;
      return format(raw, params);
    },
    [locale]
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
